import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Typography,
  Tag,
  Modal,
  message,
  Button,
  Select,
  Form,
  Spin,
  Card,
  Row,
  Col,
  Alert,
  Tooltip,
  Divider,
  Table,
  Switch,
} from "antd";
import {
  PlusOutlined,
  SearchOutlined,
  InfoCircleOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import { trackFeatureUsage } from "../lib/posthog";
import { AdminNavBar } from "../components/AdminNavBar";
import InvoiceList from "../components/invoices/InvoiceList";
import InvoiceForm from "../components/invoices/InvoiceForm";
import InvoicePreview from "../components/invoices/InvoicePreview";
import RuleSelectionInfo from "../components/invoices/RuleSelectionInfo";
import { Invoice } from "../types/invoice";
import { fetchInvoiceById } from "../services/invoiceService";
import { supabase } from "../lib/supabase";
import { Booking, Product } from "../types/booking";
import { SupabaseClient } from "@supabase/supabase-js";
import { AIR_FREIGHT } from "../lib/pricingDefaults";
import {
  createMoneybirdInvoice,
  updateMoneybirdInvoice,
  syncAllMoneybirdStatus,
} from "../services/moneybirdService";

const { Title } = Typography;
const { Option } = Select;

// Type cast supabase to avoid TypeScript errors
const typedSupabase = supabase as unknown as SupabaseClient;

const InvoicesAdmin: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [previewInvoice, setPreviewInvoice] = useState<Invoice | null>(null);
  const [previewBooking, setPreviewBooking] = useState<Booking | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [createFromBookingVisible, setCreateFromBookingVisible] =
    useState(false);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [selectedBookingId, setSelectedBookingId] = useState<number | null>(
    null
  );
  const [loadingBookings, setLoadingBookings] = useState(false);
  const [form] = Form.useForm();

  // 🧪 TESTING: Toggle between old and new pricing systems
  const [useCascadingPricing, setUseCascadingPricing] = useState(false);

  // Moneybird state
  const [moneybirdLoading, setMoneybirdLoading] = useState<{
    create: boolean;
    update: boolean;
    sync: boolean;
    syncAll: boolean;
  }>({
    create: false,
    update: false,
    sync: false,
    syncAll: false,
  });

  // Track page view on component mount
  useEffect(() => {
    trackFeatureUsage("invoices_admin");
  }, []);

  // Handle URL parameters for creating invoice from booking or showing specific invoice
  useEffect(() => {
    const createFromBookingId = searchParams.get("create_from_booking");
    const invoiceId = searchParams.get("invoice_id");

    if (createFromBookingId) {
      // Auto-open create invoice modal with specific booking
      const bookingId = parseInt(createFromBookingId, 10);
      if (bookingId && !isNaN(bookingId)) {
        console.log("Creating invoice for booking ID:", bookingId);

        // Fetch bookings first, then proceed with invoice creation
        fetchBookings().then(() => {
          // Set the booking ID and directly open the invoice creation modal
          setSelectedInvoice({ booking_id: bookingId } as Invoice);
          setCreateModalVisible(true);
        });

        // Clear the URL parameter
        setSearchParams({});
      }
    } else if (invoiceId) {
      // Auto-show specific invoice (could implement filtering in InvoiceList later)
      console.log("Showing invoice ID:", invoiceId);
      message.info(`Showing invoice #${invoiceId}`);
      // Clear the URL parameter
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);

  // Fetch bookings from Supabase
  const fetchBookings = async () => {
    try {
      setLoadingBookings(true);
      const { data, error } = await typedSupabase
        .from("bookings")
        .select(
          `
          id,
          reference,
          status,
          created_at,
          transport_type_info:booking_transport_types(*),
          booking_type_info:bookings_type(*),
          company_info:company(*),
          origin_port_info:booking_origin_port(*),
          destination_port_info:booking_destination_port(*),
          booking_incoterm_info:booking_incoterm(*),
          container_info:containers(
            id,
            reference,
            origin_port:booking_origin_port(*),
            destination_port:booking_destination_port(*)
          ),
          product_info:booking_product(*)
        `
        )
        // Fetch all bookings regardless of status
        .order("created_at", { ascending: false })
        .limit(100);

      if (error) {
        throw error;
      }

      if (data) {
        setBookings(data as unknown as Booking[]);
      }
    } catch (error) {
      console.error("Error fetching bookings:", error);
      message.error("Failed to load bookings");
    } finally {
      setLoadingBookings(false);
    }
  };

  // Get flattened product list from booking
  const getBookingProducts = (booking: Booking): Product[] => {
    if (!booking.product_info) return [];

    // Handle different structures of product_info from booking_product table
    if (Array.isArray(booking.product_info)) {
      // Already an array of products
      return booking.product_info as unknown as Product[];
    }

    // Single product
    return [booking.product_info as unknown as Product];
  };

  // Get origin port information, prioritizing container origin if available
  const getOriginPort = (booking: Booking) => {
    // Check if booking has container with origin port
    if (booking.container_info) {
      const containerInfo = Array.isArray(booking.container_info)
        ? booking.container_info[0]
        : booking.container_info;

      if (containerInfo?.origin_port) {
        const originPort = Array.isArray(containerInfo.origin_port)
          ? containerInfo.origin_port[0]
          : containerInfo.origin_port;

        if (originPort && originPort.name) {
          return {
            name: originPort.name,
            isFromContainer: true,
            containerRef: containerInfo.reference,
          };
        }
      }
    }

    // Fallback to booking origin port
    const originPort =
      booking.origin_port_info &&
      (Array.isArray(booking.origin_port_info)
        ? booking.origin_port_info[0]
        : booking.origin_port_info);

    return {
      name: originPort?.name || "Unknown",
      isFromContainer: false,
    };
  };

  // Get destination port information, prioritizing container destination if available
  const getDestinationPort = (booking: Booking) => {
    // Check if booking has container with destination port
    if (booking.container_info) {
      const containerInfo = Array.isArray(booking.container_info)
        ? booking.container_info[0]
        : booking.container_info;

      if (containerInfo?.destination_port) {
        const destPort = Array.isArray(containerInfo.destination_port)
          ? containerInfo.destination_port[0]
          : containerInfo.destination_port;

        if (destPort && destPort.name) {
          return {
            name: destPort.name,
            isFromContainer: true,
            containerRef: containerInfo.reference,
          };
        }
      }
    }

    // Fallback to booking destination port
    const destPort =
      booking.destination_port_info &&
      (Array.isArray(booking.destination_port_info)
        ? booking.destination_port_info[0]
        : booking.destination_port_info);

    return {
      name: destPort?.name || "Unknown",
      isFromContainer: false,
    };
  };

  // Calculate shipping details for a booking
  const getShippingDetails = (booking: Booking) => {
    if (!booking)
      return {
        totalWeight: 0,
        totalVolume: 0,
        volumetricWeight: 0,
        chargeableWeight: 0,
        isAirFreight: false,
      };

    // Check if it's air freight
    const isAirFreight =
      booking.transport_type_info &&
      (Array.isArray(booking.transport_type_info)
        ? booking.transport_type_info[0]?.id === 1
        : booking.transport_type_info.id === 1);

    // Get flattened products
    const products = getBookingProducts(booking);

    // Calculate total weight
    const totalWeight = products.reduce(
      (sum, product) => sum + (product.gross_weight || 0),
      0
    );

    // Calculate total volume
    const totalVolume = products.reduce(
      (sum, product) => sum + (product.cbm || 0),
      0
    );

    // Calculate volumetric weight for air freight
    const volumetricWeight = totalVolume * AIR_FREIGHT.VOLUMETRIC_FACTOR;

    // Calculate chargeable weight (higher of actual and volumetric)
    const chargeableWeight = Math.max(totalWeight, volumetricWeight);

    return {
      totalWeight,
      totalVolume,
      volumetricWeight,
      chargeableWeight,
      isAirFreight,
      cartons: products.reduce(
        (sum, product) => sum + (product.cartons || 0),
        0
      ),
      productCount: products.length,
    };
  };

  // Get incoterm from booking
  const getIncoterm = (booking: Booking) => {
    if (!booking.booking_incoterm_info) return null;

    return Array.isArray(booking.booking_incoterm_info)
      ? booking.booking_incoterm_info[0]
      : booking.booking_incoterm_info;
  };

  // Get container reference from booking
  const getContainerReference = (booking: Booking) => {
    if (!booking.container_info) return null;

    const containerInfo = Array.isArray(booking.container_info)
      ? booking.container_info[0]
      : booking.container_info;

    return containerInfo?.reference || null;
  };

  // Show create from booking modal
  const showCreateFromBookingModal = () => {
    setCreateFromBookingVisible(true);
    fetchBookings();
  };

  // Handle booking selection and proceed to invoice creation
  const handleCreateFromBooking = () => {
    if (!selectedBookingId) {
      message.error("Please select a booking");
      return;
    }

    // Close booking selection modal and open invoice creation with selected booking
    setCreateFromBookingVisible(false);
    setSelectedBookingId(null);
    form.resetFields();

    // Set the initial booking ID and show create modal
    const createWithBookingId = selectedBookingId;
    setCreateModalVisible(true);

    // We need to set this after the modal is open to ensure the form component is mounted
    setTimeout(() => {
      setSelectedInvoice({ booking_id: createWithBookingId } as Invoice);
    }, 100);
  };

  // Helper to generate booking display name
  const getBookingDisplayName = (booking: Booking) => {
    const reference = booking.reference || `EFF${booking.id}`;
    const transportType = booking.transport_type_info
      ? Array.isArray(booking.transport_type_info)
        ? booking.transport_type_info[0]?.abbreviation || ""
        : booking.transport_type_info.abbreviation || ""
      : "";

    // Get transport type ID to check if it's seafreight (transport_type 2)
    const transportTypeId = booking.transport_type_info
      ? Array.isArray(booking.transport_type_info)
        ? booking.transport_type_info[0]?.id
        : booking.transport_type_info.id
      : null;

    // Add booking type for seafreight (transport_type 2)
    let bookingTypeInfo = "";
    if (transportTypeId === 2 && booking.booking_type_info) {
      const bookingType = Array.isArray(booking.booking_type_info)
        ? booking.booking_type_info[0]?.name || ""
        : booking.booking_type_info.name || "";

      if (bookingType) {
        bookingTypeInfo = `-${bookingType}`;
      }
    }

    const company = booking.company_info
      ? Array.isArray(booking.company_info)
        ? booking.company_info[0]?.name || "Unknown"
        : booking.company_info.name || "Unknown"
      : "Unknown";

    return `${reference}${transportType}${bookingTypeInfo} - ${company}`;
  };

  // Show invoice creation modal
  const showCreateModal = () => {
    setSelectedInvoice(null);
    setCreateModalVisible(true);
  };

  // Show invoice view modal (currently unused by InvoiceList component)
  /* const showViewModal = async (invoice: Invoice) => {
    try {
      // Fetch full invoice details including lines
      const { data, error } = await fetchInvoiceById(invoice.id!);

      if (error) {
        throw error;
      }

      if (data) {
        setSelectedInvoice(data);
        setViewModalVisible(true);
      }
    } catch (error) {
      console.error("Error fetching invoice details:", error);
      message.error("Failed to load invoice details");
    }
  }; */

  // Show invoice edit modal
  const showEditModal = async (invoice: Invoice) => {
    try {
      // Fetch full invoice details including lines
      const { data, error } = await fetchInvoiceById(invoice.id!);

      if (error) {
        throw error;
      }

      if (data) {
        setSelectedInvoice(data);
        setEditModalVisible(true);
      }
    } catch (error) {
      console.error("Error fetching invoice details:", error);
      message.error("Failed to load invoice details");
    }
  };

  // Show invoice preview modal
  const showPreviewModal = async (invoice: Invoice) => {
    try {
      // Fetch full invoice details including lines
      const { data, error } = await fetchInvoiceById(invoice.id!);

      if (error) {
        throw error;
      }

      if (data) {
        setPreviewInvoice(data);

        // Find the booking associated with this invoice
        const booking = bookings.find((b) => b.id === data.booking_id);
        if (booking) {
          setPreviewBooking(booking);
        } else {
          // If booking not in current list, fetch it specifically
          const { data: bookingData, error: bookingError } = await typedSupabase
            .from("bookings")
            .select(
              `
              *,
              transport_type_info:booking_transport_types(*),
              booking_type_info:bookings_type(*),
              company_info:company(*),
              origin_port_info:booking_origin_port(*),
              destination_port_info:booking_destination_port(*),
              booking_incoterm_info:booking_incoterm(*),
              container_info:containers(
                id,
                reference,
                etd,
                eta,
                origin_port:booking_origin_port(*),
                destination_port:booking_destination_port(*)
              ),
              product_info:booking_product(*)
            `
            )
            .eq("id", data.booking_id)
            .single();

          if (bookingError) {
            console.error("Error fetching booking:", bookingError);
            message.warning("Could not load booking details for preview");
            setPreviewBooking(null);
          } else {
            setPreviewBooking(bookingData as unknown as Booking);
          }
        }

        setPreviewModalVisible(true);
      }
    } catch (error) {
      console.error("Error fetching invoice details:", error);
      message.error("Failed to load invoice details");
    }
  };

  // Handle invoice creation success
  const handleCreateSuccess = () => {
    setCreateModalVisible(false);
    setRefreshTrigger((prev) => prev + 1);
  };

  // Handle invoice edit success
  const handleEditSuccess = () => {
    setEditModalVisible(false);
    setRefreshTrigger((prev) => prev + 1);
  };

  // Refresh the invoice list
  const handleRefresh = () => {
    setRefreshTrigger((prev) => prev + 1);
  };

  // Moneybird handlers
  const handleCreateMoneybirdInvoice = async (invoice: Invoice) => {
    if (!invoice.id) return;

    try {
      setMoneybirdLoading((prev) => ({ ...prev, create: true }));

      const { data, error } = await createMoneybirdInvoice(invoice.id);

      if (error) {
        message.error(`Failed to create Moneybird invoice: ${error}`);
        return;
      }

      if (data?.success) {
        message.success(
          data.message || "Invoice successfully created in Moneybird"
        );
        if (data.moneybird_invoice_url) {
          window.open(data.moneybird_invoice_url, "_blank");
        }
        handleRefresh();
      } else {
        message.error(data?.error || "Failed to create Moneybird invoice");
      }
    } catch (error) {
      console.error("Error creating Moneybird invoice:", error);
      message.error("An unexpected error occurred");
    } finally {
      setMoneybirdLoading((prev) => ({ ...prev, create: false }));
    }
  };

  const handleUpdateMoneybirdInvoice = async (invoice: Invoice) => {
    if (!invoice.id) return;

    try {
      setMoneybirdLoading((prev) => ({ ...prev, update: true }));

      const { data, error } = await updateMoneybirdInvoice(invoice.id);

      if (error) {
        message.error(`Failed to update Moneybird invoice: ${error}`);
        return;
      }

      if (data?.success) {
        message.success(
          data.message || "Invoice successfully updated in Moneybird"
        );
        if (data.moneybird_invoice_url) {
          window.open(data.moneybird_invoice_url, "_blank");
        }
        handleRefresh();
      } else {
        message.error(data?.error || "Failed to update Moneybird invoice");
      }
    } catch (error) {
      console.error("Error updating Moneybird invoice:", error);
      message.error("An unexpected error occurred");
    } finally {
      setMoneybirdLoading((prev) => ({ ...prev, update: false }));
    }
  };

  const handleSyncAllMoneybirdStatus = async () => {
    try {
      setMoneybirdLoading((prev) => ({ ...prev, syncAll: true }));

      const { data, error } = await syncAllMoneybirdStatus();

      if (error) {
        message.error(`Failed to sync all Moneybird statuses: ${error}`);
        return;
      }

      if (data?.success && data.results) {
        const { success, failed, updated } = data.results;
        message.success(
          `Sync completed: ${success} successful, ${updated} updated, ${failed} failed`
        );
        if (failed > 0 && data.results.errors.length > 0) {
          console.error("Sync errors:", data.results.errors);
        }
        handleRefresh();
      } else {
        message.error(data?.error || "Failed to sync all Moneybird statuses");
      }
    } catch (error) {
      console.error("Error syncing all Moneybird statuses:", error);
      message.error("An unexpected error occurred");
    } finally {
      setMoneybirdLoading((prev) => ({ ...prev, syncAll: false }));
    }
  };

  // Handle edit from preview modal
  const handleEditFromPreview = () => {
    if (previewInvoice) {
      setSelectedInvoice(previewInvoice);
      setPreviewModalVisible(false);
      setEditModalVisible(true);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <AdminNavBar />

      <Title level={2}>
        Invoices
        <Tag color="warning" style={{ marginLeft: 12, fontSize: 14 }}>
          Work in progress
        </Tag>
      </Title>

      <div
        style={{ marginBottom: 16, display: "flex", gap: 8, flexWrap: "wrap" }}
      >
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={showCreateModal}
        >
          Create Invoice
        </Button>
        <Button
          type="default"
          icon={<SearchOutlined />}
          onClick={showCreateFromBookingModal}
        >
          Create from Booking
        </Button>

        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <Button
            type="default"
            icon={<SyncOutlined />}
            loading={moneybirdLoading.syncAll}
            onClick={handleSyncAllMoneybirdStatus}
          >
            Sync All Moneybird Status
          </Button>
        </div>
      </div>

      {/* 🧪 TESTING TOGGLE - SAFE */}
      <Card
        size="small"
        style={{
          marginBottom: 16,
          backgroundColor: "#f6ffed",
          border: "1px solid #b7eb8f",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <Typography.Text strong style={{ color: "#52c41a" }}>
            🧪 TESTING MODE:
          </Typography.Text>
          <Typography.Text>Use Cascading Pricing (New)</Typography.Text>
          <Switch
            checked={useCascadingPricing}
            onChange={setUseCascadingPricing}
            checkedChildren="NEW ✨"
            unCheckedChildren="OLD 📊"
          />
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {useCascadingPricing
              ? "🔗 Testing new cascading system (rules stack together)"
              : "📌 Using current system (highest priority rule wins)"}
          </Typography.Text>
        </div>
      </Card>

      <InvoiceList
        onCreateClick={showCreateModal}
        onPreviewClick={showPreviewModal}
        onEditClick={showEditModal}
        onRefresh={handleRefresh}
        onCreateMoneybirdInvoice={handleCreateMoneybirdInvoice}
        onUpdateMoneybirdInvoice={handleUpdateMoneybirdInvoice}
        moneybirdLoading={moneybirdLoading}
        key={refreshTrigger}
      />

      {/* Select Booking Modal */}
      <Modal
        title="Select a Booking"
        open={createFromBookingVisible}
        onCancel={() => setCreateFromBookingVisible(false)}
        onOk={handleCreateFromBooking}
        okText="Continue to Invoice"
        okButtonProps={{ disabled: !selectedBookingId }}
        width={600}
      >
        <Alert
          message="Pricing Components"
          description={
            <span>
              Invoice lines will be automatically generated based on pricing
              rules that match the booking's transport type, origin,
              destination, and product details. You can edit these lines in the
              next step.
            </span>
          }
          type="info"
          showIcon
          icon={<InfoCircleOutlined />}
          style={{ marginBottom: 16 }}
        />

        <Form form={form} layout="vertical">
          <Form.Item
            label="Booking"
            name="booking_id"
            rules={[{ required: true, message: "Please select a booking" }]}
          >
            <Select
              placeholder="Select a booking"
              style={{ width: "100%" }}
              showSearch
              loading={loadingBookings}
              onChange={(value: number) => setSelectedBookingId(value)}
              filterOption={(input, option) =>
                (option?.children as unknown as string)
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              notFoundContent={loadingBookings ? <Spin size="small" /> : null}
            >
              {bookings.map((booking) => (
                <Option key={booking.id} value={booking.id}>
                  {getBookingDisplayName(booking)}
                </Option>
              ))}
            </Select>
          </Form.Item>

          {selectedBookingId && (
            <div style={{ marginTop: 16 }}>
              {loadingBookings ? (
                <Spin />
              ) : (
                bookings
                  .filter((booking) => booking.id === selectedBookingId)
                  .map((booking) => (
                    <Card key={booking.id} size="small" title="Booking Details">
                      <Row gutter={16}>
                        <Col span={12}>
                          <Typography.Text strong>Reference:</Typography.Text>{" "}
                          {booking.reference || `EFF${booking.id}`}
                          {booking.transport_type_info &&
                            (Array.isArray(booking.transport_type_info)
                              ? booking.transport_type_info[0]?.abbreviation
                              : booking.transport_type_info.abbreviation)}
                          <br />
                          <Typography.Text strong>
                            Transport Type:
                          </Typography.Text>{" "}
                          {booking.transport_type_info &&
                            (Array.isArray(booking.transport_type_info)
                              ? booking.transport_type_info[0]?.name
                              : booking.transport_type_info.name)}
                          <br />
                          <Typography.Text strong>
                            Company:
                          </Typography.Text>{" "}
                          {booking.company_info &&
                            (Array.isArray(booking.company_info)
                              ? booking.company_info[0]?.name
                              : booking.company_info.name)}
                        </Col>
                        <Col span={12}>
                          <Typography.Text strong>Created:</Typography.Text>{" "}
                          {new Date(booking.created_at).toLocaleDateString()}
                          <br />
                          <Typography.Text strong>Status:</Typography.Text>{" "}
                          <Tag
                            color={
                              booking.status === "delivered"
                                ? "success"
                                : "processing"
                            }
                          >
                            {booking.status || "Unknown"}
                          </Tag>
                          <br />
                          {(() => {
                            const incoterm = getIncoterm(booking);
                            if (incoterm) {
                              return (
                                <>
                                  <Typography.Text strong>
                                    Incoterm:
                                  </Typography.Text>{" "}
                                  {incoterm.name}
                                  <br />
                                </>
                              );
                            }
                            return null;
                          })()}
                          {(() => {
                            const containerRef = getContainerReference(booking);
                            if (containerRef) {
                              return (
                                <>
                                  <Typography.Text strong>
                                    Container:
                                  </Typography.Text>{" "}
                                  {containerRef}
                                  <br />
                                </>
                              );
                            }
                            return null;
                          })()}
                          <Typography.Text strong>Route:</Typography.Text>{" "}
                          {(() => {
                            const originInfo = getOriginPort(booking);
                            return (
                              <>
                                {originInfo.name}
                                {originInfo.isFromContainer && (
                                  <Tooltip
                                    title={`From container ${
                                      originInfo.containerRef || ""
                                    }`}
                                  >
                                    <Tag
                                      color="blue"
                                      style={{
                                        marginLeft: 4,
                                        fontSize: "10px",
                                      }}
                                    >
                                      Container
                                    </Tag>
                                  </Tooltip>
                                )}
                              </>
                            );
                          })()}
                          {" → "}
                          {(() => {
                            const destInfo = getDestinationPort(booking);
                            return (
                              <>
                                {destInfo.name}
                                {destInfo.isFromContainer && (
                                  <Tooltip
                                    title={`From container ${
                                      destInfo.containerRef || ""
                                    }`}
                                  >
                                    <Tag
                                      color="blue"
                                      style={{
                                        marginLeft: 4,
                                        fontSize: "10px",
                                      }}
                                    >
                                      Container
                                    </Tag>
                                  </Tooltip>
                                )}
                              </>
                            );
                          })()}
                        </Col>
                      </Row>

                      {/* Shipping details */}
                      <Divider style={{ margin: "8px 0" }} />
                      <Row>
                        <Col span={24}>
                          <div style={{ fontSize: "12px" }}>
                            {(() => {
                              const shippingDetails =
                                getShippingDetails(booking);
                              return (
                                <Row gutter={16}>
                                  <Col span={12}>
                                    <Typography.Text strong>
                                      Weight:
                                    </Typography.Text>{" "}
                                    {shippingDetails.totalWeight.toFixed(2)} kg
                                    <br />
                                    <Typography.Text strong>
                                      Volume:
                                    </Typography.Text>{" "}
                                    {shippingDetails.totalVolume.toFixed(2)} m³
                                  </Col>
                                  {shippingDetails.isAirFreight && (
                                    <Col span={12}>
                                      <Typography.Text strong>
                                        Chargeable Weight:
                                      </Typography.Text>{" "}
                                      <Typography.Text type="danger">
                                        {shippingDetails.chargeableWeight.toFixed(
                                          2
                                        )}{" "}
                                        kg
                                      </Typography.Text>
                                      <Tooltip title="The higher of actual weight and volumetric weight (1 CBM = 167 kg)">
                                        <InfoCircleOutlined
                                          style={{
                                            marginLeft: 4,
                                            fontSize: "10px",
                                          }}
                                        />
                                      </Tooltip>
                                    </Col>
                                  )}
                                </Row>
                              );
                            })()}
                          </div>
                        </Col>
                      </Row>

                      {/* Show products list if available */}
                      {selectedBookingId && (
                        <div style={{ marginTop: 8 }}>
                          {(() => {
                            const booking = bookings.find(
                              (b) => b.id === selectedBookingId
                            );
                            if (!booking) return null;

                            const products = getBookingProducts(booking);
                            if (products.length === 0) return null;

                            return (
                              <Card
                                size="small"
                                title={`Products (${products.length})`}
                              >
                                <Table
                                  dataSource={products}
                                  size="small"
                                  pagination={false}
                                  rowKey="id"
                                  columns={[
                                    {
                                      title: "Name",
                                      dataIndex: "name",
                                      key: "name",
                                      width: "40%",
                                      render: (text) => (
                                        <Typography.Text
                                          ellipsis={{ tooltip: text }}
                                        >
                                          {text}
                                        </Typography.Text>
                                      ),
                                    },
                                    {
                                      title: "Cartons",
                                      dataIndex: "cartons",
                                      key: "cartons",
                                      align: "right",
                                      render: (cartons) => cartons || 0,
                                    },
                                    {
                                      title: "Weight (kg)",
                                      dataIndex: "gross_weight",
                                      key: "gross_weight",
                                      align: "right",
                                      render: (weight) =>
                                        (weight || 0).toFixed(2),
                                    },
                                    {
                                      title: "Volume (m³)",
                                      dataIndex: "cbm",
                                      key: "cbm",
                                      align: "right",
                                      render: (cbm) => (cbm || 0).toFixed(3),
                                    },
                                  ]}
                                  summary={(pageData) => {
                                    // Calculate column totals
                                    const totalCartons = pageData.reduce(
                                      (sum, product) =>
                                        sum + (product.cartons || 0),
                                      0
                                    );
                                    const totalWeight = pageData.reduce(
                                      (sum, product) =>
                                        sum + (product.gross_weight || 0),
                                      0
                                    );
                                    const totalVolume = pageData.reduce(
                                      (sum, product) =>
                                        sum + (product.cbm || 0),
                                      0
                                    );

                                    return (
                                      <Table.Summary.Row>
                                        <Table.Summary.Cell index={0}>
                                          <Typography.Text strong>
                                            Total
                                          </Typography.Text>
                                        </Table.Summary.Cell>
                                        <Table.Summary.Cell
                                          index={1}
                                          align="right"
                                        >
                                          <Typography.Text strong>
                                            {totalCartons}
                                          </Typography.Text>
                                        </Table.Summary.Cell>
                                        <Table.Summary.Cell
                                          index={2}
                                          align="right"
                                        >
                                          <Typography.Text strong>
                                            {totalWeight.toFixed(2)}
                                          </Typography.Text>
                                        </Table.Summary.Cell>
                                        <Table.Summary.Cell
                                          index={3}
                                          align="right"
                                        >
                                          <Typography.Text strong>
                                            {totalVolume.toFixed(3)}
                                          </Typography.Text>
                                        </Table.Summary.Cell>
                                      </Table.Summary.Row>
                                    );
                                  }}
                                />
                              </Card>
                            );
                          })()}
                        </div>
                      )}
                    </Card>
                  ))
              )}
            </div>
          )}
        </Form>
      </Modal>

      {/* Create Invoice Modal */}
      <Modal
        title="Create New Invoice"
        open={createModalVisible}
        onCancel={() => setCreateModalVisible(false)}
        footer={null}
        width={1200}
        style={{ top: 20 }}
      >
        <InvoiceForm
          onSuccess={handleCreateSuccess}
          onCancel={() => setCreateModalVisible(false)}
          initialBookingId={selectedInvoice?.booking_id}
        />
      </Modal>

      {/* View Invoice Modal */}
      <Modal
        title={`Invoice ${
          selectedInvoice?.id || ""
        } - Pricing Decision Analysis`}
        open={viewModalVisible}
        onCancel={() => setViewModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setViewModalVisible(false)}>
            Close
          </Button>,
        ]}
        width={1000}
      >
        {selectedInvoice &&
          (() => {
            // Find the booking for this invoice
            const booking = bookings.find(
              (b) => b.id === selectedInvoice.booking_id
            );
            const companyId = booking?.company_info
              ? Array.isArray(booking.company_info)
                ? booking.company_info[0]?.id
                : booking.company_info.id
              : undefined;

            return (
              <div>
                {booking ? (
                  <RuleSelectionInfo booking={booking} companyId={companyId} />
                ) : (
                  <Alert
                    type="warning"
                    message="Booking data not available"
                    description="Cannot display rule analysis without booking information."
                    showIcon
                  />
                )}
                {/* Future: Add other invoice details here */}
              </div>
            );
          })()}
      </Modal>

      {/* Edit Invoice Modal */}
      <Modal
        title={`Edit Invoice ${selectedInvoice?.id || ""}`}
        open={editModalVisible}
        onCancel={() => setEditModalVisible(false)}
        footer={null}
        width={1200}
      >
        {selectedInvoice && (
          <InvoiceForm
            onSuccess={handleEditSuccess}
            onCancel={() => setEditModalVisible(false)}
            editInvoice={selectedInvoice}
          />
        )}
      </Modal>

      {/* Preview Invoice Modal */}
      <Modal
        title={`Invoice Preview - ${previewInvoice?.id || ""}`}
        open={previewModalVisible}
        onCancel={() => setPreviewModalVisible(false)}
        footer={[
          <Button key="edit" type="primary" onClick={handleEditFromPreview}>
            Edit Invoice
          </Button>,
          <Button key="close" onClick={() => setPreviewModalVisible(false)}>
            Close
          </Button>,
        ]}
        width={800}
        style={{ top: 20 }}
      >
        {previewInvoice && (
          <InvoicePreview invoice={previewInvoice} booking={previewBooking} />
        )}
      </Modal>
    </div>
  );
};

export default InvoicesAdmin;
